import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { ManagementEntityEventModel, ManagementEntrySummaryModel } from '@data-layer/models/management';
import { DependencyModel } from '@data-layer/models/dependencies';
import { ReplaySubject } from 'rxjs';
import { WidgetsService } from '@services/widgets.service';
import { TranslateService } from '@ngx-translate/core';
import { DependenciesRepository, EntrySummaryMessagesRepository } from '@data-layer/repositories';
import {
  enumEntrySummaryState,
  enumEntrySummarySubmissionType,
  enumEntrySummaryType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import { EntityModel } from '@data-layer/models/entities';
import {
  CBP_MESSAGE,
  CONSIGNEE_NOT_FOUND,
  FILER_CODE_NOT_AUTHORIZED,
  IMPORTER_NOT_FOUND,
  MANUFACTURER_NOT_FOUND,
} from '@constants/cbp-messages';
import { DependencyHelper } from '@helpers/dependency.helper';
import { enumDependantEntityType } from '@data-layer/enums/dependencies';
import { finalize, take } from 'rxjs/operators';
import { DependenciesService } from '@services/dependencies.service';
import { EntrySummaryValidationService } from '@services/entry-summary-validation.service';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { GenericService } from '@services/generic.service';
import { UtilsHelper } from '@helpers/utils-helper';
import { ValidationMessageModel } from '@data-layer/models/common';
import { enumPaymentState } from '@data-layer/enums/payments';
import { BillItemPendingModel } from '@data-layer/models/payments';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';

@Component({
  selector: 'es-card-notice',
  templateUrl: './es-card-notice.component.html',
  styleUrls: ['./es-card-notice.component.scss'],
})
export class EsCardNoticeComponent implements OnChanges, OnDestroy {
  @Input() public es: EntrySummaryModel | ManagementEntrySummaryModel;
  @Input() public pendingBills: BillItemPendingModel[] = [];
  @Input() public disableActions: boolean = false;
  @Input() public showContactTeem: boolean = true;
  @Input() public isOpenFiles: boolean = false;
  @Input() public showChatWithBroker: boolean = false;
  @Input() public showBackToEntry: boolean = false;
  @Input() public showCardBody: boolean = true;
  @Input() public showCustomStateTooltip: boolean = false;
  @Input() public showHeaderOnDraft: boolean = false;
  @Output() public isOpenFilesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public goToBrokerChat: EventEmitter<any> = new EventEmitter<any>();
  @Output() public backToEntry: EventEmitter<any> = new EventEmitter<any>();
  @Output() public messagesLoaded: EventEmitter<any> = new EventEmitter<any>();

  public cardBodyText: any = null;
  public cardHeaderAttention: any = null;
  public cardBodyAttention: any = null;
  public state: enumEntrySummaryState;
  public enumEntrySummaryState: typeof enumEntrySummaryState = enumEntrySummaryState;
  public isShowSupport: any = null;
  public cbpMessages: any[] = [];
  public cbpLogMessages: ManagementEntityEventModel[] = [];
  public errorMessages: any[] = [];
  public isShowLoader: boolean = false;
  public isShowAttention: boolean = false;
  public isShowNotice: boolean = true;
  public isShowCbpLog: boolean = false;
  public isShowCbpLogLoader: boolean = false;
  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public openDutiesModal: boolean = false;
  public enumEntrySummaryType: typeof enumEntrySummaryType = enumEntrySummaryType;
  public enumTransportationMode: typeof enumTransportationMode = enumTransportationMode;

  public importerDep: DependencyModel;
  public consigneeDep: DependencyModel;
  public manufacturerDep: DependencyModel;

  private isDeleted: boolean = false;
  private readonly deletedStates: enumEntrySummaryState[] = [
    enumEntrySummaryState.CBP_DELETED,
    enumEntrySummaryState.CBP_DELETE_PENDING,
    enumEntrySummaryState.DELETED,
  ];
  private readonly CBPMessagesStates: enumEntrySummaryState[] = [
    enumEntrySummaryState.REJECTED,
    enumEntrySummaryState.NO_BILL_ON_FILE,
    enumEntrySummaryState.ACCEPTED,
  ];
  private readonly errorMessagesStates: enumEntrySummaryState[] = [
    enumEntrySummaryState.NOT_VALID_PROVIDER,
    enumEntrySummaryState.UNRECOVERABLE,
  ];
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();
  private errorCodes: Array<string | number> = [];

  constructor(
    private widgetsService: WidgetsService,
    private translate: TranslateService,
    private dependenciesRepository: DependenciesRepository,
    private entrySummaryMessagesRepository: EntrySummaryMessagesRepository,
    private entrySummaryValidationService: EntrySummaryValidationService,
    private genericService: GenericService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.es) {
      this.initData();
    }

    if (changes.pendingBills && this.showCardBody) {
      this.getBodyText();
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public get isShowCustomStateTooltip(): boolean {
    return (
      this.showCustomStateTooltip &&
      this.es.state === enumEntrySummaryState.CUSTOM &&
      !!this.es.customState &&
      !!this.es.customState.internalNotes
    );
  }

  public get isBrokerEntry(): boolean {
    return this.es.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED;
  }

  public get isShowCbpLogButton(): boolean {
    return (
      this.es.submissionType !== enumEntrySummarySubmissionType.BROKER_ASSISTED &&
      this.es.state !== enumEntrySummaryState.NOT_SENT &&
      this.cbpLogMessages.length > 0
    );
  }

  public get isShowButtonOpenFiles(): boolean {
    return this.es.submissionType !== enumEntrySummarySubmissionType.BROKER_ASSISTED;
  }

  public get isShowBrokerChatLink(): boolean {
    return this.showChatWithBroker && this.es.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED;
  }

  public get hasNewBrokerMessages(): boolean {
    return 'hasNewBrokerMessages' in this.es && this.es.hasNewBrokerMessages;
  }

  public get isShowBackToEntryLink(): boolean {
    return !this.showChatWithBroker && this.showBackToEntry;
  }

  public get importer(): EntityModel {
    return this.es.shipments && this.es.shipments.length > 0 ? this.es.shipments[0].importer : null;
  }

  public get consignee(): EntityModel {
    let result = null;
    for (const shipment of this.es.shipments || []) {
      if (shipment.consignee) {
        result = shipment.consignee;
      }
    }
    return result;
  }

  public get manufacturer() {
    let result = null;
    for (const shipment of this.es.shipments || []) {
      for (const invoice of shipment.invoices || []) {
        if (invoice.manufacturer) {
          result = invoice.manufacturer;
        }
      }
    }
    return result;
  }

  public get showText(): boolean {
    const isErrorState = [
      enumEntrySummaryState.NOT_VALID_PROVIDER,
      enumEntrySummaryState.UNRECOVERABLE,
      enumEntrySummaryState.REJECTED,
    ].includes(this.es.state);
    if (isErrorState) {
      const isHasErrorMessages = UtilsHelper.concat<ValidationMessageModel>(this.cbpMessages, this.errorMessages).length > 0;
      return !!this.cardBodyText && isHasErrorMessages;
    }
    return !!this.cardBodyText;
  }

  public get errorColor(): boolean {
    return this.errorMessagesStates.includes(this.es.state);
  }

  public get isPaymentPending(): boolean {
    return this.es?.paymentState === enumPaymentState.PENDING;
  }

  public get isPaymentDemandExist(): boolean {
    return (
      this.es?.state === enumEntrySummaryState.BROKER_PROCESSING_FAILED &&
      (this.pendingBills || []).reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price * curr.quantity, 0) > 0
    );
  }

  public get isHasInstantPayment(): boolean {
    return (this.pendingBills || []).some((bill: BillItemPendingModel) => bill?.paymentType === enumCompanyPaymentType.INSTANT);
  }

  public showTranslatedKey(messageKey: string) {
    if (
      ![
        CBP_MESSAGE(IMPORTER_NOT_FOUND),
        CBP_MESSAGE(CONSIGNEE_NOT_FOUND),
        CBP_MESSAGE(MANUFACTURER_NOT_FOUND),
        'ENTRY_ERROR_MESSAGES.MANUFACTURER_NOT_REGISTERED',
      ].includes(messageKey)
    ) {
      return true;
    }
    const dep = this.getDepByKey(messageKey);
    return !dep;
  }

  public getEntityTypeText(messageKey: string) {
    const code = DependencyHelper.getEntityTypeByKey(messageKey);
    return DependencyHelper.getEntityTypeText(code);
  }

  public showRegisterButton(messageKey: string) {
    return DependencyHelper.showRegisterButton(messageKey, this.getDepByKey(messageKey));
  }

  public registerButtonText(messageKey: string) {
    return DependencyHelper.registerButtonText(messageKey, this.getDepByKey(messageKey));
  }

  public registerMessage(messageKey: string) {
    return DependencyHelper.registerMessage(this.getDepByKey(messageKey));
  }

  public registerLink(messageKey: string) {
    const code = DependencyHelper.getEntityTypeByKey(messageKey);
    return DependencyHelper.registerLink(code, enumDependantEntityType.ENTRY_SUMMARY, this.es, this.getDepEntityByKey(messageKey).id);
  }

  public showAbiFormLink(messageKey: string) {
    return messageKey === CBP_MESSAGE(FILER_CODE_NOT_AUTHORIZED);
  }

  public toggleCbpLog() {
    this.isShowCbpLog = !this.isShowCbpLog;
  }

  public onGoToBrokerChat() {
    this.goToBrokerChat.emit();
  }

  public onBackToEntry() {
    this.backToEntry.emit();
  }

  public contactToSupport() {
    const { referenceNumber } = this.es;
    const data: any = { referenceNumber };
    if (this.errorCodes && this.errorCodes.length) {
      data.errors = this.errorCodes;
    }
    this.widgetsService.trackData('Contact team:', data);
    this.widgetsService.openWidget(['es']);
  }

  private initData() {
    this.state = this.es.state;
    this.cardBodyText = null;
    this.cardHeaderAttention = null;
    this.cardBodyAttention = null;
    this.isShowSupport = null;
    this.cbpMessages = [];
    this.errorMessages = [];
    this.cbpLogMessages = [];
    this.isShowLoader = false;
    this.isShowAttention = false;
    this.isShowNotice = true;
    this.isShowCbpLog = false;
    this.checkShowNotice();
    this.getShowAction();
    if (this.showCardBody) {
      this.getBodyText();
    }
    this.isDeleted = this.deletedStates.indexOf(this.es.state) !== -1;
    this.errorCodes = [];
    if (this.showCardBody) {
      this.fetchCBPMessages();
      this.getCbpLogMessages();
      this.fetchErrorMessages();
    }
  }

  private getCbpLogMessages() {
    if (this.es.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED) {
      return;
    }
    if (this.cbpLogMessages.length === 0) {
      this.isShowCbpLogLoader = true;
      this.entrySummaryMessagesRepository
        .getCbpRawMessages(this.es.id, false, this.genericService.isPrinting$.getValue())
        .pipe(
          finalize(() => {
            this.isShowCbpLogLoader = false;
            this.cdr.markForCheck();
          }),
          take(1),
        )
        .subscribe((messages) => {
          this.cbpLogMessages = messages;
        });
    }
  }

  private fetchCBPMessages() {
    const isCBPMessagesState = this.CBPMessagesStates.indexOf(this.es.state) !== -1;

    if (isCBPMessagesState) {
      this.isShowLoader = true;
      this.entrySummaryMessagesRepository
        .getCbpMessages(this.es.id, false, this.genericService.isPrinting$.getValue())
        .pipe(
          finalize(() => {
            this.messagesLoaded.emit(true);
            this.isShowLoader = false;
            this.cdr.markForCheck();
          }),
          take(1),
        )
        .subscribe((data) => {
          this.cbpMessages = UtilsHelper.uniqBy<any>(
            data
              .map(
                (message: { key: string; params: string[] }) => {
                  const keyCode = +message.key;
                  this.logIfNan(keyCode, CBP_MESSAGE('ERROR_CODE'), message.key);
                  const params = {
                    entityType: '',
                    entityIdentifier: '',
                  };
                  const cbpMessage: { [key: string]: string; translate?: string } = {};
                  if (message.params.length) {
                    this.translate
                      .get('INFO.' + message.params[0] + '.TITLE')
                      .pipe(take(1))
                      .subscribe((res: string) => {
                        params.entityType = res;
                      });
                    params.entityIdentifier = message.params[1];
                  }
                  this.translate
                    .get(CBP_MESSAGE(message.key))
                    .pipe(take(1))
                    .subscribe((res) => {
                      if (res.includes('CBP_MESSAGES') || res.includes('INFO')) {
                        console.log(res);
                        this.errorCodes.push(message.key);
                      }
                      cbpMessage.translate = res;
                    });
                  return { ...cbpMessage, params, key: CBP_MESSAGE(message.key) };
                },
                () => {
                  this.isShowLoader = false;
                },
              )
              .filter((message) => {
                return !message.translate.includes('CBP_MESSAGES') && !message.translate.includes('INFO');
              })
              .filter((message) => {
                if (message.key === CBP_MESSAGE(CONSIGNEE_NOT_FOUND)) {
                  return (
                    this.importer.entityIdentifier !== this.consignee.entityIdentifier &&
                    this.importer.entityIdentifierQualifier === this.consignee.entityIdentifierQualifier
                  );
                }
                return true;
              }),
            'key',
          );

          this.updateDependency();
        });
    } else {
      this.messagesLoaded.emit(true);
    }
  }

  private fetchErrorMessages() {
    const isErrorState = this.errorMessagesStates.indexOf(this.es.state) !== -1;

    if (isErrorState) {
      this.isShowLoader = true;
      this.entrySummaryMessagesRepository
        .getLdsExceptions(this.es.id, false, this.genericService.isPrinting$.getValue())
        .pipe(
          finalize(() => {
            this.isShowLoader = false;
            this.cdr.markForCheck();
          }),
          take(1),
        )
        .subscribe((data) => {
          this.isShowLoader = false;

          this.errorMessages = UtilsHelper.uniqBy<any>(
            data
              .map(
                (message) => {
                  const keyCode = +message.errorCode;
                  this.logIfNan(keyCode, 'CBP_MESSAGES.ERROR_CODE', message.errorCode);
                  const errorMessage: { [key: string]: string; translate?: string } = {};

                  const params = this.entrySummaryValidationService.getErrorParams(message, this.es);

                  this.translate
                    .get('ENTRY_ERROR_MESSAGES.' + message.errorCode, params)
                    .pipe(take(1))
                    .subscribe((res) => {
                      if (res.includes('ENTRY_ERROR_MESSAGES')) {
                        console.log(res);
                        this.errorCodes.push(message.errorCode);
                      }
                      errorMessage.translate = res;
                    });
                  return { ...errorMessage, key: 'ENTRY_ERROR_MESSAGES.' + message.errorCode, params };
                },
                () => {
                  this.isShowLoader = false;
                },
              )
              .filter((message) => {
                return !message.translate.includes('ENTRY_ERROR_MESSAGES');
              }),
            'key',
          );

          this.updateDependency();
        });
    }
  }

  private checkShowNotice() {
    this.isShowNotice = this.es.state !== enumEntrySummaryState.NOT_SENT;
  }

  private getBodyText() {
    switch (this.es.state) {
      case enumEntrySummaryState.AWAITING_ARRIVAL:
        this.cardBodyText = 'STATUS.CARD_TEXT.AWAITING_ARRIVAL';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
        this.cardBodyText = 'STATUS_ES.DESCRIPTION.AWAITING_RESPONSE';
        break;
      case enumEntrySummaryState.DUTY_CALCULATION_COMPLETE:
        this.cardBodyText = 'STATUS.CARD_TEXT.DUTY_CALCULATION_COMPLETE';
        break;
      case enumEntrySummaryState.BOND_PENDING:
        this.cardBodyText = 'STATUS.CARD_TEXT.BOND_PENDING';
        break;
      case enumEntrySummaryState.SENT_TO_PROVIDER:
      case enumEntrySummaryState.VALIDATED_AT_PROVIDER:
      case enumEntrySummaryState.PROVIDER_SEND_PENDING:
        this.cardBodyText = 'STATUS.CARD_TEXT.DRAFT_CBY_V_SEND';
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
        if (this.isPaymentDemandExist) {
          this.cardBodyText = this.isHasInstantPayment
            ? 'STATUS.CARD_TEXT.BROKER_PROCESSING_FAILED_PAYMENT_DEMAND_HEADER_INSTANT'
            : 'STATUS.CARD_TEXT.BROKER_PROCESSING_FAILED_PAYMENT_DEMAND_HEADER_MONTHLY';
        } else {
          this.cardBodyText = 'STATUS.CARD_TEXT.BROKER_PROCESSING_FAILED';
        }
        break;
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHED:
        this.cardBodyText = 'STATUS.CARD_TEXT.BROKER_PROCESSING_FINISHED';
        break;
      case enumEntrySummaryState.UNRECOVERABLE:
        this.cardBodyText = 'STATUS_ES.DESCRIPTION.UNRECOVERABLE';
        break;
      case enumEntrySummaryState.NOT_VALID_PROVIDER:
        this.cardBodyText = 'STATUS_ES.DESCRIPTION.UNRECOVERABLE';
        break;
      case enumEntrySummaryState.SENT_TO_CBP:
        this.cardBodyText = 'STATUS.CARD_TEXT.DRAFT_CBP_PEND';
        break;
      case enumEntrySummaryState.REJECTED:
        this.cardBodyText = 'STATUS.CARD_TEXT.REJECTED_CBP';
        break;
      case enumEntrySummaryState.ACCEPTED:
        this.cardBodyText = 'STATUS.CARD_TEXT.ACCEPTED_CBP_ES';
        break;
      case enumEntrySummaryState.NO_BILL_ON_FILE:
        this.cardBodyText = 'STATUS.CARD_TEXT.NO_BILL_ON_FILE';
        break;
      case enumEntrySummaryState.APPROVED:
        this.cardBodyText = 'STATUS.CARD_TEXT.APPROVED_ES';
        break;
      case enumEntrySummaryState.DUTY_CALCULATION_PENDING:
        this.cardBodyText = 'STATUS.CARD_TEXT.DUTY_CALCULATION_PENDING';
        break;
      case enumEntrySummaryState.CBP_DELETE_PENDING:
        this.cardBodyText = 'STATUS.CARD_TEXT.REMOVAL_PENDING';
        break;
      case enumEntrySummaryState.CBP_DELETED:
        this.cardBodyText = 'STATUS.CARD_TEXT.CBP_DELETED';
        break;
      case enumEntrySummaryState.DELETED:
        this.cardBodyText = 'STATUS.CARD_TEXT.DELETED';
        break;
      case enumEntrySummaryState.CUSTOM:
        this.cardBodyText = this.es.customState.description;
        break;
      default:
        this.cardBodyText = null;
        break;
    }
  }

  private getShowAction() {
    if (this.isShowSupport !== null) {
      return;
    }

    switch (this.es.state) {
      case enumEntrySummaryState.NOT_VALID_PROVIDER:
      case enumEntrySummaryState.UNRECOVERABLE:
      case enumEntrySummaryState.REJECTED:
      case enumEntrySummaryState.NO_BILL_ON_FILE:
        this.isShowSupport = true;
        break;
      default:
        this.isShowSupport = false;
        break;
    }
  }

  private updateDependency() {
    const depMessages = [
      CBP_MESSAGE(IMPORTER_NOT_FOUND),
      CBP_MESSAGE(CONSIGNEE_NOT_FOUND),
      CBP_MESSAGE(MANUFACTURER_NOT_FOUND),
      'ENTRY_ERROR_MESSAGES.MANUFACTURER_NOT_REGISTERED',
    ];
    const hasDepMessage = UtilsHelper.concat<any>(this.cbpMessages, this.errorMessages).some((mess) => depMessages.includes(mess.key));

    if (hasDepMessage) {
      this.dependenciesRepository
        .getDependenciesForES(this.es.id)
        .pipe(take(1))
        .subscribe((dep) => {
          this.importerDep = this.importer ? DependenciesService.findImporterDep(dep, this.importer) : null;
          this.consigneeDep = this.consignee ? DependenciesService.findConsigneeDep(dep, this.consignee) : null;
          this.manufacturerDep = this.manufacturer ? DependenciesService.findManufacturerDep(dep, this.manufacturer) : null;
        });
    }
  }

  private getDepEntityByKey(messageKey: string): EntityModel {
    return messageKey === CBP_MESSAGE(IMPORTER_NOT_FOUND)
      ? this.importer
      : messageKey === CBP_MESSAGE(CONSIGNEE_NOT_FOUND)
      ? this.consignee
      : [CBP_MESSAGE(MANUFACTURER_NOT_FOUND), 'ENTRY_ERROR_MESSAGES.MANUFACTURER_NOT_REGISTERED'].includes(messageKey)
      ? this.manufacturer
      : null;
  }

  private getDepByKey(messageKey: string) {
    return messageKey === CBP_MESSAGE(IMPORTER_NOT_FOUND)
      ? this.importerDep
      : messageKey === CBP_MESSAGE(CONSIGNEE_NOT_FOUND)
      ? this.consigneeDep
      : [CBP_MESSAGE(MANUFACTURER_NOT_FOUND), 'ENTRY_ERROR_MESSAGES.MANUFACTURER_NOT_REGISTERED'].includes(messageKey)
      ? this.manufacturerDep
      : null;
  }

  private logIfNan(keyCode: number, message: string, code: string) {
    if (!isNaN(keyCode)) {
      this.translate
        .get(message, { code: code })
        .pipe(take(1))
        .subscribe((res) => {
          console.log(res);
          this.errorCodes.push(code);
        });
    }
  }
}
